import { useState, useCallback, useEffect } from "react";

const OPEN_EVENT = "cleo:open-drawer";
const OPEN_CUSTOMER_EVENT = "cleo:open-customer-update";

let pendingCustomerId: number | null = null;

export function getPendingCleoCustomerId(): number | null {
  return pendingCustomerId;
}

export function consumePendingCleoCustomerId(): number | null {
  const id = pendingCustomerId;
  pendingCustomerId = null;
  return id;
}

/** Dispatch from anywhere to open the Cleo drawer (must be user-initiated). */
export function openCleoDrawer(): void {
  window.dispatchEvent(new CustomEvent(OPEN_EVENT));
}

export function openCleoCustomerUpdate(customerId: number): void {
  pendingCustomerId = customerId;
  window.dispatchEvent(new CustomEvent(OPEN_CUSTOMER_EVENT, { detail: { customerId } }));
  openCleoDrawer();
}

/**
 * Survives React remounts within the SPA (route changes, layout remounts).
 * Resets to closed on full page load / refresh / new login tab.
 */
let persistedOpen = false;

function readPersistedOpen(): boolean {
  return persistedOpen;
}

function writePersistedOpen(open: boolean): void {
  persistedOpen = open;
}

/** Test-only: reset module persistence between cases. */
export function __resetCleoDrawerPersistedOpenForTests(): void {
  persistedOpen = false;
}

/**
 * Manages Cleo drawer open/close state.
 * - Closed by default (including after refresh / login).
 * - Never auto-opens from signals, notifications, navigation, or init.
 * - Only explicit open/close (badge click, dashboard CTA, close controls) change state.
 * - Last explicit state is kept across in-app navigation / remounts.
 */
export function useCleoDrawerState() {
  const [isOpen, setIsOpen] = useState(readPersistedOpen);

  const open = useCallback(() => {
    writePersistedOpen(true);
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    writePersistedOpen(false);
    setIsOpen(false);
  }, []);

  useEffect(() => {
    const handler = () => {
      writePersistedOpen(true);
      setIsOpen(true);
    };
    window.addEventListener(OPEN_EVENT, handler);
    window.addEventListener(OPEN_CUSTOMER_EVENT, handler);
    return () => {
      window.removeEventListener(OPEN_EVENT, handler);
      window.removeEventListener(OPEN_CUSTOMER_EVENT, handler);
    };
  }, []);

  /** Same as close — kept for drawer UI label compatibility. */
  const closeForToday = close;

  return { isOpen, open, close, closeForToday };
}
